import React from 'react';
import { motion } from 'framer-motion';
import { Layout } from '../components/layout/Layout';
import { PageHero, SEOContent } from '../components/shared';
import { ContactForm } from '../components/shared/ContactForm';
import { pageHeroImages, kontaktDaten, sprechzeiten, notfallsprechstunde } from '../data/mock';
import { Phone, Mail, MapPin, Clock, Car, Accessibility, Navigation } from 'lucide-react';

export const TerminPage = () => {
  return (
    <Layout>
      <PageHero
        title="Wir sind für Sie da"
        subtitle="Vereinbaren Sie Ihren Termin in der Gemeinschaftspraxis Dres. von Bergh im Medicum Diez. Wir freuen uns auf Ihren Besuch."
        backgroundImage={pageHeroImages.termin}
        showCTA={false}
      />

      <section className="py-16 lg:py-20 bg-[#eff0f0]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left Column - Info */}
            <div className="space-y-8">
              {/* Sprechzeiten */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg border border-[#0b382a]/10"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-[#0b382a] rounded-full flex items-center justify-center">
                    <Clock className="w-5 h-5 text-[#ddc57b]" />
                  </div>
                  <h2 className="text-xl font-semibold text-[#0b382a]">Sprechzeiten</h2>
                </div>
                <p className="text-[#0b382a]/70 mb-4">Planen Sie Ihren Besuch</p>
                <div className="space-y-3">
                  {sprechzeiten.map((item, index) => (
                    <div key={index} className="flex justify-between py-2 border-b border-[#0b382a]/5 last:border-0">
                      <span className="font-medium text-[#0b382a]">{item.tag}</span>
                      <span className="text-[#0b382a]/70 text-right">{item.zeit}</span>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 pt-6 border-t border-[#0b382a]/10">
                  <h3 className="font-semibold text-[#0b382a] mb-3">Notfallsprechstunde (ohne Termin)</h3>
                  <div className="space-y-2">
                    {notfallsprechstunde.map((item, index) => (
                      <div key={index} className="flex justify-between">
                        <span className="text-[#0b382a]/70">{item.tag}</span>
                        <span className="text-[#b5a05f] font-medium">{item.zeit}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-[#0b382a]/50 mt-2">Die reguläre Terminsprechstunde beginnt im Anschluss.</p>
                </div>
              </motion.div>

              {/* Kontakt & Anfahrt */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg border border-[#0b382a]/10"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-[#0b382a] rounded-full flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-[#ddc57b]" />
                  </div>
                  <h2 className="text-xl font-semibold text-[#0b382a]">Kontakt & Erreichbarkeit</h2>
                </div>
                <p className="text-[#0b382a]/70 mb-6">Die Gemeinschaftspraxis Dres. von Bergh befindet sich im modernen Gesundheitszentrum Medicum Diez.</p>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-[#0b382a] mb-1">Anschrift</h4>
                    <p className="text-[#0b382a]/70">
                      Medicum Diez<br />
                      {kontaktDaten.adresse.strasse}<br />
                      {kontaktDaten.adresse.plz} {kontaktDaten.adresse.ort}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-[#b5a05f]" />
                    <a href={`tel:${kontaktDaten.telefon.replace(/\s/g, '')}`} className="text-[#0b382a] hover:text-[#b5a05f] transition-colors font-medium">
                      {kontaktDaten.telefon}
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-[#b5a05f]" />
                    <a href={`mailto:${kontaktDaten.email}`} className="text-[#0b382a] hover:text-[#b5a05f] transition-colors">
                      {kontaktDaten.email}
                    </a>
                  </div>
                </div>
              </motion.div>

              {/* Vorteile */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg border border-[#0b382a]/10"
              >
                <h2 className="text-xl font-semibold text-[#0b382a] mb-6">Ihr Vorteil bei uns</h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#0b382a]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Car className="w-5 h-5 text-[#0b382a]" />
                    </div>
                    <div>
                      <h4 className="font-medium text-[#0b382a]">Kostenlose Parkplätze</h4>
                      <p className="text-sm text-[#0b382a]/70">Direkt vor dem Medicum Diez stehen Ihnen zahlreiche kostenlose Parkplätze zur Verfügung.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#0b382a]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Accessibility className="w-5 h-5 text-[#0b382a]" />
                    </div>
                    <div>
                      <h4 className="font-medium text-[#0b382a]">Barrierefreiheit</h4>
                      <p className="text-sm text-[#0b382a]/70">Unsere Praxis ist vollständig barrierefrei zugänglich. Ein Aufzug bringt Sie bequem in unsere Räumlichkeiten.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#0b382a]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Navigation className="w-5 h-5 text-[#0b382a]" />
                    </div>
                    <div>
                      <h4 className="font-medium text-[#0b382a]">Zentrale Lage</h4>
                      <p className="text-sm text-[#0b382a]/70">Optimale Erreichbarkeit aus Diez, Limburg und dem gesamten Umland. Bushaltestelle: „Werkes“.</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Right Column - Form */}
            <div>
              <ContactForm sourcePage="4" sourceTitle="Termin & Kontakt" />
            </div>
          </div>
        </div>
      </section>

      <SEOContent
        title="Kontakt Hausarzt Diez Limburg"
        content="Suchen Sie einen Hausarzt oder Kardiologen im Raum Diez und Limburg? Kontaktieren Sie die Gemeinschaftspraxis Dres. von Bergh im Medicum Diez für eine persönliche Beratung und zeitnahe Termine."
      />
    </Layout>
  );
};

export default TerminPage;
